export const generateOutfitService = async (wardrobe, formData) => {
    if (process.env.NODE_ENV !== 'production') {
        console.log("[OUTFIT-SERVICE] Début de la génération");
        console.log("[OUTFIT-SERVICE] Garde-robe:", wardrobe.length, "vêtements");
        console.log("[OUTFIT-SERVICE] FormData:", formData);
    }
    
    const clothesByType = {
        tops: wardrobe.filter(item => ["t-shirt", "sweat", "veste"].includes(item.type)),
        bottoms: wardrobe.filter(item => ["pantalon"].includes(item.type)),
        shoes: wardrobe.filter(item => ["chaussures"].includes(item.type))
    };

    // Vérifier s'il y a suffisamment de vêtements dans chaque catégorie
    if (process.env.NODE_ENV !== 'production') {
        console.log("[OUTFIT-SERVICE] Vêtements disponibles:", {
            tops: clothesByType.tops.length,
            bottoms: clothesByType.bottoms.length,
            shoes: clothesByType.shoes.length
        });
    }
    
    if (!clothesByType.tops.length || !clothesByType.bottoms.length || !clothesByType.shoes.length) {
        throw new Error("Pas assez de vêtements dans votre garde-robe pour créer une tenue");
    }

    // Call relative API path; CRA dev server proxies '/api/*' to backend
    const apiUrl = '/api/generate-outfit';
    
    if (process.env.NODE_ENV !== 'production') {
        console.log("[OUTFIT-SERVICE] Appel API:", apiUrl);
    }

    const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ wardrobe, formData })
    });

    const data = await response.json();
    if (process.env.NODE_ENV !== 'production') {
        console.log("[OUTFIT-SERVICE] Réponse API reçue:", data);
    }
    
    if (!data.choices?.[0]?.message?.content) {
        console.error("[OUTFIT-SERVICE] Réponse invalide:", data);
        throw new Error("Réponse invalide de l'IA");
    }

    const cleanedResponse = data.choices[0].message.content
        .replace(/```json|```/g, '')
        .trim();
    
    if (process.env.NODE_ENV !== 'production') {
        console.log("[OUTFIT-SERVICE] Contenu nettoyé:", cleanedResponse);
    }

    let outfitSelection;
    try {
        outfitSelection = JSON.parse(cleanedResponse);
        if (process.env.NODE_ENV !== 'production') {
            console.log("[OUTFIT-SERVICE] Outfit parsé:", outfitSelection);
        }
    } catch (error) {
        console.error("[OUTFIT-SERVICE] Erreur de parsing:", error);
        throw new Error("Réponse de l'IA mal formatée");
    }

    // Vérifier que les IDs retournés existent dans la garde-robe
    const allIds = wardrobe.map(item => item.id);
    if (process.env.NODE_ENV !== 'production') {
        console.log("[OUTFIT-SERVICE] IDs disponibles:", allIds);
        console.log("[OUTFIT-SERVICE] IDs sélectionnés par l'IA:", outfitSelection);
    }
    
    const isValid = ['top', 'bottom', 'shoes'].every(key =>
        allIds.includes(outfitSelection[key])
    );

    if (!isValid) {
        // Fallback: créer une sélection aléatoire si l'IA retourne des IDs invalides
        if (process.env.NODE_ENV !== 'production') {
            console.warn("[OUTFIT-SERVICE] IDs invalides détectés, utilisation du fallback aléatoire");
        }
        outfitSelection = generateFallbackOutfit(clothesByType, formData);
        console.log("[OUTFIT-SERVICE] Fallback outfit:", outfitSelection);
    }
    
    // Vérifier si la couleur demandée est respectée
    if (formData.color) {
        const selectedItems = {
            top: wardrobe.find(item => item.id === outfitSelection.top),
            bottom: wardrobe.find(item => item.id === outfitSelection.bottom),
            shoes: wardrobe.find(item => item.id === outfitSelection.shoes)
        };
        
        const colorMatchCount = Object.values(selectedItems).filter(item => {
            if (!item) return false;
            const colors = Array.isArray(item.colors) ? item.colors : [item.color];
            return colors.some(c => c && c.toLowerCase().includes(formData.color.toLowerCase()));
        }).length;
        
        if (process.env.NODE_ENV !== 'production') {
            console.log(`[OUTFIT-SERVICE] Vérification couleur ${formData.color}: ${colorMatchCount}/3 vêtements correspondent`);
        }
        
        // Si moins de 2 vêtements de la bonne couleur, forcer la correction
        if (colorMatchCount < 2) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn(`[OUTFIT-SERVICE] ⚠️ L'IA n'a pas respecté la couleur ${formData.color}! Correction...`);
            }
            outfitSelection = generateColorMatchingOutfit(clothesByType, formData.color, wardrobe);
            if (process.env.NODE_ENV !== 'production') {
                console.log("[OUTFIT-SERVICE] Outfit corrigé pour la couleur:", outfitSelection);
            }
        }
    }

    if (process.env.NODE_ENV !== 'production') {
        console.log("[OUTFIT-SERVICE] Outfit final retourné:", outfitSelection);
    }
    return outfitSelection;
};

// Fonction pour générer une tenue en respectant la couleur demandée
const generateColorMatchingOutfit = (clothesByType, requestedColor, wardrobe) => {
    const matchColor = (item) => {
        const colors = Array.isArray(item.colors) ? item.colors : [item.color];
        return colors.some(c => c && c.toLowerCase().includes(requestedColor.toLowerCase()));
    };
    
    const colorMatchingClothes = {
        tops: clothesByType.tops.filter(matchColor),
        bottoms: clothesByType.bottoms.filter(matchColor),
        shoes: clothesByType.shoes.filter(matchColor)
    };
    
    const getRandomItem = (items) => items[Math.floor(Math.random() * items.length)];
    
    // Essayer de prendre au moins 2 vêtements de la couleur demandée
    let outfit = {};
    let colorCount = 0;
    
    // Top
    if (colorMatchingClothes.tops.length > 0) {
        outfit.top = getRandomItem(colorMatchingClothes.tops).id;
        colorCount++;
    } else {
        outfit.top = getRandomItem(clothesByType.tops).id;
    }
    
    // Bottom
    if (colorMatchingClothes.bottoms.length > 0 && colorCount < 2) {
        outfit.bottom = getRandomItem(colorMatchingClothes.bottoms).id;
        colorCount++;
    } else if (colorMatchingClothes.bottoms.length > 0 && Math.random() > 0.5) {
        outfit.bottom = getRandomItem(colorMatchingClothes.bottoms).id;
        colorCount++;
    } else {
        outfit.bottom = getRandomItem(clothesByType.bottoms).id;
    }
    
    // Shoes
    if (colorMatchingClothes.shoes.length > 0 && colorCount < 2) {
        outfit.shoes = getRandomItem(colorMatchingClothes.shoes).id;
        colorCount++;
    } else if (colorMatchingClothes.shoes.length > 0 && Math.random() > 0.5) {
        outfit.shoes = getRandomItem(colorMatchingClothes.shoes).id;
    } else {
        outfit.shoes = getRandomItem(clothesByType.shoes).id;
    }
    
    return outfit;
};

// Fonction de secours pour générer une tenue aléatoirement
const generateFallbackOutfit = (clothesByType, formData) => {
    // Si une couleur est demandée, utiliser generateColorMatchingOutfit
    if (formData?.color) {
        // Reconstruire wardrobe à partir de clothesByType
        const wardrobe = [...clothesByType.tops, ...clothesByType.bottoms, ...clothesByType.shoes];
        return generateColorMatchingOutfit(clothesByType, formData.color, wardrobe);
    }
    
    const getRandomItem = (items) => items[Math.floor(Math.random() * items.length)];

    return {
        top: getRandomItem(clothesByType.tops).id,
        bottom: getRandomItem(clothesByType.bottoms).id,
        shoes: getRandomItem(clothesByType.shoes).id
    };
};
